// US1945335: Hook for fetching and mutating global feature toggles
import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchFeatureToggles,
  patchFeatureToggle,
} from "../connections/GlobalConfig";
import usePolling from "./usePolling";
import { POLLING_INTERVALS } from "../config/polling";

/**
 * useGlobalToggles - Background-refresh hook for global feature toggles.
 *
 * Follows the project's Background Refresh Pattern:
 * - `loading` is true only during the initial fetch (no spinner on refresh)
 * - `refreshing` is true during background polling passes
 * - `lastUpdated` records the timestamp of the most recent successful fetch
 * - `error` is cleared on success; background failures preserve existing data
 *
 * @param {Object} options
 * @param {boolean} [options.enabled=true] - Whether polling is active
 * @returns {{
 *   toggles: Object,
 *   loading: boolean,
 *   refreshing: boolean,
 *   lastUpdated: Date|null,
 *   error: Error|null,
 *   refresh: function,
 *   flip: function
 * }}
 *
 * @example
 * const { toggles, loading, refreshing, lastUpdated, error, refresh, flip } =
 *   useGlobalToggles({ enabled: userPrivileges?.deity });
 */
export default function useGlobalToggles({ enabled = true } = {}) {
  const [toggles, setToggles] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const fetchData = useCallback(
    async (isBackgroundRefresh = false) => {
      if (!enabled) return;

      if (isBackgroundRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // Cancel any in-flight request before starting a new one
      abortRef.current?.abort();
      abortRef.current = new AbortController();

      try {
        const data = await fetchFeatureToggles({
          signal: abortRef.current.signal,
        });
        setToggles(data?.toggles || {});
        setLastUpdated(new Date());
        setError(null);
      } catch (err) {
        if (err.name === "AbortError") {
          // Request was cancelled; not an error condition
          return;
        }
        if (isBackgroundRefresh) {
          // Keep existing data visible; re-throw so usePolling can apply backoff
          console.warn("Global toggles background refresh failed:", err);
          throw err;
        } else {
          setError(err);
        }
      } finally {
        if (isBackgroundRefresh) {
          setRefreshing(false);
        } else {
          setLoading(false);
        }
      }
    },
    [enabled]
  );

  // Initial fetch on mount; cancel on unmount
  useEffect(() => {
    fetchData(false);
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchData]);

  // Background polling — pauses when the browser tab is hidden
  usePolling(() => fetchData(true), POLLING_INTERVALS.GLOBAL_TOGGLES_REFRESH, {
    enabled,
    pauseWhenHidden: true,
  });

  /**
   * Optimistically merge the backend's returned toggle map after a PATCH so
   * the UI updates immediately without waiting for the next polling tick.
   *
   * @param {string} name - Toggle name
   * @param {boolean} newEnabled - Desired enabled state
   * @returns {Promise<Object>} Full updated toggle map from the server
   */
  const flip = useCallback(async (name, newEnabled) => {
    const result = await patchFeatureToggle(name, newEnabled);
    if (result?.toggles) {
      setToggles((prev) => ({ ...prev, ...result.toggles }));
      setLastUpdated(new Date());
    }
    return result;
  }, []);

  return {
    toggles,
    loading,
    refreshing,
    lastUpdated,
    error,
    refresh: () => fetchData(true),
    flip,
  };
}
